import { RootState } from 'data/store/store';
import {
  Member,
  Permissions,
  MemberId,
  Country,
  Address,
  HomeId,
  InviteToken,
} from 'types';
import { PlanType } from 'lib/plans';

export const NAME = 'data/HOME_HUB';
export const LOAD_HOME_HUB_DATA = `${NAME}/LOAD`;
export const ONBOARD_HOME_HUB_DATA = `${NAME}/ONBOARD`;
export const JOIN_HOME_HUB = `${NAME}/JOIN`;
export const REMOVE_FROM_HOME_HUB = `${NAME}/REMOVE`;
export const SET_HOME_HUB_DATA = `${NAME}/SET`;
export const CHANGE_HOME_ADDRESS = `${NAME}/CHANGE_HOME_ADDRESS`;
export const TRIGGER_PARENTAL_CONTROL_CHANGE = `${NAME}/TRIGGER_PARENTAL_CONTROL_CHANGE`;
export const CHANGE_PARENTAL_CONTROL = `${NAME}/CHANGE_PARENTAL_CONTROL`;

export type HomeHubData = {
  name: string;
  planType: PlanType;
  maxCapacity: number;
  inviteToken: InviteToken;
  address: Address | null;
  homeId: HomeId;
  members: Array<Member>;
  userCountry: Country;
  permissions: Permissions;
};

export type HomeHubState = {
  isLoading: boolean;
  isOnboarding: boolean;
  isJoining: boolean;
  isRemoving: boolean;
  isChangingHomeAddress: boolean;
  isChangingParentalControl: boolean;
  data: HomeHubData | null;
};

type SetHomeHubDataAction = {
  type: typeof SET_HOME_HUB_DATA;
  data: HomeHubData | null;
};

export const setHomeHubData = (
  data: HomeHubData | null,
): SetHomeHubDataAction => ({
  type: SET_HOME_HUB_DATA,
  data,
});

type LoadHomeHubDataAction = {
  type: typeof LOAD_HOME_HUB_DATA;
};

export const loadHomeHubData = () => ({
  type: LOAD_HOME_HUB_DATA,
});

type OnboardHomeHubDataAction = {
  type: typeof ONBOARD_HOME_HUB_DATA;
};

export const onboardHomeHubData = (): OnboardHomeHubDataAction => ({
  type: ONBOARD_HOME_HUB_DATA,
});

type JoinHomeHubAction = {
  type: typeof JOIN_HOME_HUB;
};

export const joinHomeHub = (): JoinHomeHubAction => ({
  type: JOIN_HOME_HUB,
});

type RemoveFromHomeHubAction = {
  type: typeof REMOVE_FROM_HOME_HUB;
};

export const removeFromHomeHub = (): RemoveFromHomeHubAction => ({
  type: REMOVE_FROM_HOME_HUB,
});

type ChangeHomeAddressAction = {
  type: typeof CHANGE_HOME_ADDRESS;
};

export const changeHomeAddress = (): ChangeHomeAddressAction => ({
  type: CHANGE_HOME_ADDRESS,
});

type TriggerParentalControlChangeAction = {
  type: typeof TRIGGER_PARENTAL_CONTROL_CHANGE;
};

export const triggerParentalControlChange =
  (): TriggerParentalControlChangeAction => ({
    type: TRIGGER_PARENTAL_CONTROL_CHANGE,
  });

type ChangeParentalControlAction = {
  type: typeof CHANGE_PARENTAL_CONTROL;
  targetMemberId: MemberId;
  explicitContentLockEnabled: boolean;
};

export const changeParentalControl = (
  targetMemberId: MemberId,
  explicitContentLockEnabled: boolean,
) => ({
  type: CHANGE_PARENTAL_CONTROL,
  targetMemberId,
  explicitContentLockEnabled,
});

export const initialState: HomeHubState = {
  isLoading: true,
  isOnboarding: false,
  isJoining: false,
  isRemoving: false,
  isChangingHomeAddress: false,
  isChangingParentalControl: false,
  data: null,
};

export const updateMemberInData =
  (data: HomeHubData | null) =>
  (
    changeCallback: (member: Member) => Member,
    whichMember: (member: Member) => boolean = () => true,
  ) => {
    return data
      ? {
          ...data,
          members: data.members.map(member => {
            if (whichMember(member)) {
              return changeCallback(member);
            }
            return member;
          }),
        }
      : data;
  };

type HomeHubActionTypes =
  | SetHomeHubDataAction
  | LoadHomeHubDataAction
  | OnboardHomeHubDataAction
  | JoinHomeHubAction
  | ChangeHomeAddressAction
  | TriggerParentalControlChangeAction
  | ChangeParentalControlAction;

export const reducer = (state = initialState, action: HomeHubActionTypes) => {
  switch (action.type) {
    case LOAD_HOME_HUB_DATA:
      return {
        ...state,
        isLoading: true,
        isRemoving: false,
      };

    case ONBOARD_HOME_HUB_DATA:
      return {
        ...state,
        isOnboarding: true,
      };

    case CHANGE_HOME_ADDRESS:
      return {
        ...state,
        isChangingHomeAddress: true,
      };

    case JOIN_HOME_HUB:
      return {
        ...state,
        isJoining: true,
      };

    case REMOVE_FROM_HOME_HUB:
      return {
        ...state,
        isRemoving: true,
      };

    case TRIGGER_PARENTAL_CONTROL_CHANGE:
      return {
        ...state,
        isChangingParentalControl: true,
      };

    case CHANGE_PARENTAL_CONTROL: {
      const updateMember = updateMemberInData(state.data);
      return {
        ...state,
        isChangingParentalControl: false,
        data: updateMember(
          member => ({
            ...member,
            explicitContentLockEnabled: (action as ChangeParentalControlAction)
              .explicitContentLockEnabled,
          }),
          member =>
            member.id ===
            (action as ChangeParentalControlAction).targetMemberId,
        ),
      };
    }

    case SET_HOME_HUB_DATA:
      return {
        ...state,
        isLoading: false,
        isOnboarding: false,
        isJoining: false,
        isRemoving: false,
        isChangingHomeAddress: false,
        isChangingParentalControl: false,
        data: (action as SetHomeHubDataAction).data,
      };

    default:
      return state;
  }
};
export const selector = (state: RootState) => state[NAME];
